import type { Vibe } from './zod.js';

/**
 * Each vibe is a FLAVOR within the Nuzum Method signature.
 *
 * The signature owns the obsidian/moss/paper base, the river-stone radii,
 * the technical-serif type system, the drift motion, the blueprint labels,
 * and the cinematic photo treatment. Vibes only get to choose:
 *
 *   - 3 leak colors (used as large blurred radial gradients behind content)
 *   - The accent color (CTAs)
 *   - Specific display + italic font picks (the body sans + label monospace
 *     stay constant across vibes for cohesion)
 *
 * This file is the structured TypeScript source of truth that the wizard
 * (VibeStep + PrintableOptionSet + future flavor exports) reads from.
 *
 * The CSS source of truth is master-scaffold/src/styles/signature.css.
 * Keep the two in lockstep. scripts/check-contrast.mjs verifies the CSS
 * side hits WCAG AA against obsidian, paper, AND a worst-case leak overlay.
 */
export interface VibeFlavor {
  vibe: Vibe;
  /** Three leak colors. Order matters - leak-1 is the dominant one. */
  leaks: readonly [string, string, string];
  /** Primary accent color (buttons, links, focus rings). */
  accent: string;
  /** Text color shown ON the accent (must clear AA against `accent`). */
  onAccent: string;
  /** Display headline font stack. */
  displayFont: string;
  /** Italic / serif accent font stack. */
  italicFont: string;
  /** Adjective shown in the wizard preview (matches VIBE_COPY[v].feel). */
  feelHint: string;
  /**
   * One-line note about how this vibe's accent behaves under NM. Read by
   * the PaletteStep so the operator understands what they can / cannot
   * customize for this vibe.
   */
  accentBehavior: string;
}

export const VIBE_FLAVOR: Readonly<Record<Vibe, VibeFlavor>> = {
  'The Bold': {
    vibe: 'The Bold',
    leaks: ['#F97316', '#FCD34D', '#FB923C'],
    accent: '#F97316',
    onAccent: '#1A0F08',
    displayFont: "'Archivo Black', 'Space Grotesk', sans-serif",
    italicFont: "'PT Serif', Georgia, serif",
    feelHint: 'Loud · Confident · Direct',
    accentBehavior: 'Ember orange. Override only if the client has a brand red/orange that must match.',
  },
  'The Professional': {
    vibe: 'The Professional',
    leaks: ['#1D4ED8', '#3B82F6', '#818CF8'],
    accent: '#3B82F6',
    onAccent: '#0A0F1F',
    displayFont: "'Source Serif 4', 'Cormorant Garamond', Georgia, serif",
    italicFont: "'Source Serif 4', Georgia, serif",
    feelHint: 'Trustworthy · Editorial · Established',
    accentBehavior: 'Mid-blue. Override only if the firm has a corporate navy/teal to honor.',
  },
  'The Modern': {
    vibe: 'The Modern',
    leaks: ['#06B6D4', '#818CF8', '#34D399'],
    accent: '#06B6D4',
    onAccent: '#021018',
    displayFont: "'Plus Jakarta Sans', 'Space Grotesk', sans-serif",
    italicFont: "'Cormorant Garamond', Georgia, serif",
    feelHint: 'Fluid · Technical · Forward',
    accentBehavior: 'Deep cyan. Tuned darker than vivid #22D3EE so muted text on leak-overlay clears AA.',
  },
  'The Artisan': {
    vibe: 'The Artisan',
    leaks: ['#B8542F', '#E8C9A5', '#8B5A2B'],
    accent: '#8B3F22',
    onAccent: '#F4EFE6',
    displayFont: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
    italicFont: "'Fraunces', Georgia, serif",
    feelHint: 'Warm · Lived-in · Handmade',
    accentBehavior: 'Darker terracotta paired with bone-paper text so it clears AA on light buttons.',
  },
  'The Wellness': {
    vibe: 'The Wellness',
    leaks: ['#6B8E5A', '#D4A39D', '#A8B89E'],
    accent: '#6B8E5A',
    onAccent: '#0F1A0B',
    displayFont: "'Cormorant Garamond', Georgia, serif",
    italicFont: "'Cormorant Garamond', Georgia, serif",
    feelHint: 'Calm · Generous · Trustworthy',
    accentBehavior: 'Sage. Override if the client wants a different botanical (eucalyptus, dusty rose).',
  },
  'The Editorial': {
    vibe: 'The Editorial',
    leaks: ['#B0102A', '#F4EFE6', '#525252'],
    accent: '#B0102A',
    onAccent: '#F4EFE6',
    displayFont: "'Playfair Display', 'Cormorant Garamond', Georgia, serif",
    italicFont: "'Playfair Display', Georgia, serif",
    feelHint: 'Premium · Authored · Deliberate',
    accentBehavior: 'Editorial red. The single fierce color in an otherwise paper-and-ink palette.',
  },
  'The Luxe': {
    vibe: 'The Luxe',
    leaks: ['#C9A961', '#8C7A4A', '#F5EBD3'],
    accent: '#C9A961',
    onAccent: '#0A0A0A',
    displayFont: "'Cormorant Garamond', Georgia, serif",
    italicFont: "'Cormorant Garamond', Georgia, serif",
    feelHint: 'Quiet · Considered · Expensive-feeling',
    accentBehavior: 'Champagne gold. Override only with a different precious metal (silver, rose-gold).',
  },
  'The Tech': {
    vibe: 'The Tech',
    leaks: ['#4338CA', '#818CF8', '#06B6D4'],
    accent: '#818CF8',
    onAccent: '#0A0E1F',
    displayFont: "'Space Grotesk', 'Inter', sans-serif",
    italicFont: "'Cormorant Garamond', Georgia, serif",
    feelHint: 'Engineered · Sharp · Optimistic',
    accentBehavior: 'Lavender on indigo leaks. Reads as a product team that ships.',
  },
  'The Heritage': {
    vibe: 'The Heritage',
    leaks: ['#722F37', '#1A3A2E', '#C9A961'],
    accent: '#C9A961',
    onAccent: '#1A0E0F',
    displayFont: "'Crimson Pro', 'Cormorant Garamond', Georgia, serif",
    italicFont: "'Crimson Pro', Georgia, serif",
    feelHint: 'Established · Crafted · Considered',
    accentBehavior: 'Aged gold over burgundy + forest leaks. Old-world craft on modern bones.',
  },
};

/** Convenience: all vibes in display order. */
export const ALL_VIBES = [
  'The Bold',
  'The Professional',
  'The Modern',
  'The Artisan',
  'The Wellness',
  'The Editorial',
  'The Luxe',
  'The Tech',
  'The Heritage',
] as const;
